import TYPES from './types';

import { IWarrantyError } from './reducers';
import {
  accountNewPasswordRepeatSel,
  accountCurrentPasswordSel,
  accountNewPasswordSel
} from './selectors';
import {
  INewPasswordValidation,
  IAddressValidation
} from 'src/model/modules/accountview.model';
import UserService from 'src/services/user/userService';
import ShipAddressModel from 'src/model/shipAddress.model';
import AccountShipService from 'src/services/account/accountShipService';
import { all } from 'q';
import accountServiceInstance from 'src/services/account/accountService';
import i18n from 'i18next';

const INITIAL_ADDRESS_VALIDATION: IAddressValidation = {
  customerName: { invalid: false, message: '' },
  address: { invalid: false, message: '' },
  city: { invalid: false, message: '' },
  country: { invalid: false, message: '' },
  state: { invalid: false, message: '' },
  zipCode: { invalid: false, message: '' }
};

const setIsPending = (isPending: boolean) => {
  return {
    type: TYPES.SET_IS_PENDING,
    payload: isPending ? isPending : false
  };
};

const setError = (error?: IWarrantyError) => {
  return {
    type: TYPES.SET_ERROR,
    payload: error
      ? error
      : {
          hasFailed: false,
          message: ''
        }
  };
};

const setMenuOption = (option: string) => {
  return {
    type: TYPES.SET_MENU_OPTION,
    payload: option
  };
};

const setCurrentPassword = (value: string) => {
  return {
    type: TYPES.SET_CURRENT_PASSWORD,
    payload: value
  };
};

const rawSetNewPassword = (value: string) => {
  return {
    type: TYPES.SET_NEW_PASSWORD,
    payload: value
  };
};

const rawSetNewPasswordRepeat = (value: string) => {
  return {
    type: TYPES.SET_NEW_PASSWORD_REPEAT,
    payload: value
  };
};

export const validatePasswords = (pass1: string, pass2: string) => {
  const validation = {
    valid: false,
    dirty: false,
    message: ''
  };
  if (pass1.length && pass2.length) {
    validation.valid = true;
    validation.dirty = true;
    if (pass1 !== pass2) {
      validation.valid = false;
      validation.message = "Passwords don't match";
    } else if (pass1.length < 8 || pass2.length < 8) {
      validation.valid = false;
      validation.message = 'Minimum 8 characters';
    }
  } else {
    validation.valid = false;
    validation.dirty = false;
    validation.message = '';
  }

  return validation;
};

const setNewPassword = (value: string) => {
  return (dispatch: any, getState: any) => {
    // Validate new password and repeat
    const repeat = accountNewPasswordRepeatSel(getState());
    const validation = validatePasswords(value, repeat);
    dispatch(setNewPasswordValidation(validation));
    dispatch(rawSetNewPassword(value));
  };
};

const setNewPasswordRepeat = (value: string) => {
  return (dispatch: any, getState: any) => {
    // Validate new password and repeat
    const newpass = accountNewPasswordSel(getState());
    const validation = validatePasswords(value, newpass);
    dispatch(setNewPasswordValidation(validation));
    dispatch(rawSetNewPasswordRepeat(value));
  };
};

const setNewPasswordValidation = (validation: INewPasswordValidation) => {
  return {
    type: TYPES.SET_NEW_PASSWORD_VALIDATION,
    payload: validation
  };
};

const changePassword = (cb?: (result: boolean) => void) => {
  return async (dispatch: any, getState: any) => {
    const state = getState();
    const currentPassword = accountCurrentPasswordSel(state);
    const newPassword = accountNewPasswordSel(state);
    dispatch(setError());
    dispatch(setIsPending(true));
    try {
      const result = await UserService.changePassword(
        currentPassword,
        newPassword
      );
      if (cb) {
        cb(result);
      }
      dispatch(setIsPending(false));
    } catch (err) {
      console.error(err);
      dispatch(
        setError({ hasFailed: true, message: "Couldn't change password" })
      );
      dispatch(setIsPending(false));
    }
  };
};

const setShipRequestAddress = (address?: ShipAddressModel) => {
  return {
    type: TYPES.SET_SHIP_ADDRESS,
    payload: address
      ? address
      : {
          address: '',
          address2: '',
          addressId: 0,
          city: '',
          country: '',
          customerName: '',
          userId: 0,
          state: '',
          zipCode: ''
        }
  };
};

const setShipAddressValidation = (address?: IAddressValidation) => {
  return {
    type: TYPES.SET_SHIP_ADDRESS_VALIDATION,
    payload: address ? address : INITIAL_ADDRESS_VALIDATION
  };
};

const setShipRequestSuccess = (success: boolean) => {
  return {
    type: TYPES.SET_SHIP_ADDRESS_SUCCED,
    payload: success ? success : false
  };
};

const setShipRequestFailure = (failure: boolean) => {
  return {
    type: TYPES.SET_SHIP_ADDRESS_FAILED,
    payload: failure ? failure : false
  };
};

const checkAddressInvalid = (address: ShipAddressModel) => {
  let invalidRequest: boolean = false;
  let addressValidation: IAddressValidation = INITIAL_ADDRESS_VALIDATION;
  if (address.customerName === '') {
    addressValidation = {
      ...addressValidation,
      customerName: {
        invalid: true,
        message: i18n.t('common.fieldRequiredTxt')
      }
    };
    invalidRequest = true;
  }
  if (address.address === '') {
    addressValidation = {
      ...addressValidation,
      address: { invalid: true, message: i18n.t('common.fieldRequiredTxt') }
    };
    invalidRequest = true;
  }
  if (address.city === '') {
    addressValidation = {
      ...addressValidation,
      city: { invalid: true, message: i18n.t('common.fieldRequiredTxt') }
    };
    invalidRequest = true;
  }
  if (address.country === '') {
    addressValidation = {
      ...addressValidation,
      country: { invalid: true, message: i18n.t('common.fieldRequiredTxt') }
    };
    invalidRequest = true;
  }
  if (address.state === '') {
    addressValidation = {
      ...addressValidation,
      state: { invalid: true, message: i18n.t('common.fieldRequiredTxt') }
    };
    invalidRequest = true;
  }
  if (address.zipCode === '') {
    addressValidation = {
      ...addressValidation,
      zipCode: { invalid: true, message: i18n.t('common.fieldRequiredTxt') }
    };
    invalidRequest = true;
  }
  return { invalidRequest, addressValidation };
};

const shipAddRequest = (params: ShipAddressModel) => {
  const invalid = checkAddressInvalid(params);
  return (dispatch: any) => {
    if (!invalid.invalidRequest) {
      dispatch(setIsPending(true));
      dispatch(setShipRequestSuccess(false));
      dispatch(setShipRequestFailure(false));
      AccountShipService.addShipAddress({
        address: params.address,
        address2: params.address2,
        city: params.city,
        country: params.country,
        customerName: params.customerName,
        customerNumber: params.customerNumber,
        state: params.state,
        zipCode: params.zipCode
      })
        .then(res => {
          dispatch(setShipRequestAddress());
          dispatch(setShipRequestSuccess(true));
          dispatch(setIsPending(false));
        })
        .catch(e => {
          dispatch(setShipRequestAddress(params));
          dispatch(setShipRequestFailure(true));
          dispatch(setIsPending(false));
        });
    } else {
      dispatch(setShipAddressValidation(invalid.addressValidation));
    }
  };
};

const shipChangeRequest = (params: ShipAddressModel) => {
  const invalid = checkAddressInvalid(params);
  return (dispatch: any) => {
    if (!invalid.invalidRequest) {
      dispatch(setIsPending(true));
      dispatch(setShipRequestSuccess(false));
      AccountShipService.changeShipAddress({
        address: params.address,
        address2: params.address2,
        addressId: params.addressId,
        city: params.city,
        country: params.country,
        customerName: params.customerName,
        customerNumber: params.customerNumber,
        state: params.state,
        zipCode: params.zipCode
      })
        .then(res => {
          dispatch(setShipRequestAddress());
          dispatch(setShipRequestSuccess(true));
          dispatch(setIsPending(false));
        })
        .catch(e => {
          dispatch(setShipRequestAddress(params));
          dispatch(setShipRequestFailure(true));
          dispatch(setIsPending(false));
        });
    } else {
      dispatch(setShipAddressValidation(invalid.addressValidation));
    }
  };
};

const shipRemovalRequest = (params: {
  addressId: number;
  customerNumber: number;
}) => {
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    dispatch(setShipRequestSuccess(false));
    AccountShipService.removeShipAddress({
      addressId: params.addressId,
      customerNumber: params.customerNumber
    })
      .then(res => {
        dispatch(setShipRequestAddress());
        dispatch(setShipRequestSuccess(true));
        dispatch(setIsPending(false));
      })
      .catch(e => {
        dispatch(setShipRequestFailure(true));
        dispatch(setIsPending(false));
      });
  };
};

const deliveryStatus = (value: any) => {
  return {
    type: TYPES.SET_DELIVERY_STATUS,
    payload: value
  };
};

const getDeliveryStatus = () => {
  return (dispatch: any) => {
    accountServiceInstance
      .getDeliveryStatus()
      .then(res => {
        dispatch(deliveryStatus(res));
      })
      .catch(e => {
        dispatch(deliveryStatus({ accountId: '', deliverCompleteFlag: false }));
      });
  };
};

const creditCardStatus = (value: any) => {
  return {
    type: TYPES.SET_CC_STATUS,
    payload: value
  };
};

const getCreditCardStatus = () => {
  return (dispatch: any) => {
    accountServiceInstance.getCreditCardFlagStatus().then(res => {
      dispatch(creditCardStatus(res));
    });
  };
};

export default {
  setError,
  setIsPending,
  setMenuOption,
  setCurrentPassword,
  setNewPassword,
  setNewPasswordRepeat,
  changePassword,
  setNewPasswordValidation,
  shipAddRequest,
  shipChangeRequest,
  shipRemovalRequest,
  setShipRequestAddress,
  setShipRequestSuccess,
  setShipRequestFailure,
  setShipAddressValidation,
  deliveryStatus,
  getDeliveryStatus,
  creditCardStatus,
  getCreditCardStatus
};
